// ============================================================
// importService - Kapsamlı JSON yedek geri yükleme servisi
// Tüm tabloları doğru sırayla, ID eşleştirme ve hata yönetimiyle içe aktarır
// ============================================================

import { supabase } from './supabase';
import { getUserBusinessId } from './supabaseDb';

// ============================================================
// TYPES
// ============================================================

export interface ImportTableResult {
  table: string;
  imported: number;
  updated: number;
  total: number;
  skipped: number;
  errors: string[];
}

export interface ImportBackupResult {
  success: boolean;
  tables: ImportTableResult[];
  summary: {
    totalImported: number;
    totalUpdated: number;
    totalSkipped: number;
    totalErrors: number;
  };
  customerIdMap: Record<string, string>;
  noteIdMap: Record<string, string>;
}

// ============================================================
// HELPERS
// ============================================================

function handleError(err: any): string {
  if (!err) return 'Bilinmeyen hata';
  return err.message || err.details || String(err);
}

function toNumber(value: any): number {
  if (value === null || value === undefined) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function nullifyEmptyDates(record: Record<string, any>, dateFields: string[]): void {
  for (const field of dateFields) {
    if (record[field] === '' || record[field] === null || record[field] === 'null') {
      record[field] = null;
    }
  }
}

function sanitizeForTable(record: Record<string, any>, validColumns: Set<string>): Record<string, any> {
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(record)) {
    if (validColumns.has(key) && value !== undefined) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

// ============================================================
// TABLE COLUMN DEFINITIONS (gerçek DB kolonları)
// ============================================================

const TABLE_COLUMNS: Record<string, Set<string>> = {
  customers: new Set([
    'id', 'full_name', 'phone', 'second_phone', 'address', 'city', 'district',
    'identity_number', 'notes', 'status', 'total_sales_amount', 'total_paid_amount',
    'total_debt_amount', 'last_purchase_date', 'created_at', 'updated_at',
    'business_id', 'customer_no',
  ]),
  products: new Set([
    'id', 'name', 'category', 'product_type', 'brand', 'model_code',
    'serial_number', 'color', 'size', 'purchase_price', 'sale_price',
    'stock_quantity', 'critical_stock_level', 'supplier', 'location',
    'image_url', 'description', 'status', 'created_at', 'updated_at', 'business_id',
  ]),
  sales: new Set([
    'id', 'sale_no', 'customer_id', 'customer_name', 'sale_date',
    'product_category', 'payment_type', 'subtotal', 'discount_amount',
    'total_amount', 'down_payment', 'paid_amount', 'remaining_amount',
    'installment_count', 'first_payment_date', 'payment_status',
    'delivery_status', 'delivery_address', 'warranty_period', 'notes',
    'created_by', 'created_at', 'updated_at', 'business_id',
  ]),
  sale_items: new Set([
    'id', 'sale_id', 'product_id', 'product_name', 'category',
    'product_type', 'brand', 'model_code', 'color', 'size',
    'quantity', 'unit_price', 'total_price', 'created_at', 'business_id', 'updated_at',
  ]),
  payments: new Set([
    'id', 'customer_id', 'customer_name', 'sale_id', 'sale_no',
    'payment_date', 'amount', 'payment_method', 'receipt_no',
    'description', 'received_by', 'created_at', 'business_id', 'updated_at',
    'is_legacy_payment', 'promissory_note_id',
  ]),
  installments: new Set([
    'id', 'customer_id', 'sale_id', 'due_date', 'amount', 'paid_amount',
    'status', 'paid_date', 'notes', 'created_at', 'updated_at', 'business_id',
  ]),
  deliveries: new Set([
    'id', 'sale_no', 'sale_id', 'customer_id', 'customer_name',
    'customer_phone', 'product_name', 'product_category', 'delivery_date',
    'delivery_time', 'delivery_person', 'status', 'address', 'district',
    'notes', 'completed_at', 'payment_status', 'created_at', 'updated_at', 'business_id',
  ]),
  promissory_notes: new Set([
    'id', 'business_id', 'note_no', 'note_book_no', 'note_page_no',
    'customer_id', 'customer_name', 'sale_id', 'sale_no', 'amount',
    'due_date', 'status', 'paid_amount', 'paid_date',
    'debtor_name', 'debtor_address', 'debtor_identity',
    'guarantor_name', 'guarantor_address', 'guarantor_identity',
    'issue_date', 'issue_place', 'notes', 'created_by',
    'created_at', 'updated_at', 'note_sequence_no',
    'customer_phone', 'guarantor_phone', 'source_type',
    'legacy_note_no', 'purchase_date', 'remaining_amount',
    'description', 'is_legacy_payment', 'enforcement_status', 'enforcement_case_id',
  ]),
  enforcement_cases: new Set([
    'id', 'business_id', 'case_no', 'customer_id', 'promissory_note_id',
    'sale_id', 'customer_no', 'customer_name', 'customer_phone',
    'total_debt', 'paid_after_enforcement', 'remaining_debt',
    'enforcement_date', 'last_contact_date', 'assigned_lawyer',
    'lawyer_phone', 'lawyer_file_no', 'court_office', 'official_file_no',
    'status', 'description', 'result_notes', 'closed_at',
    'created_by', 'created_at', 'updated_at',
  ]),
  expenses: new Set([
    'id', 'expense_no', 'category', 'description', 'amount',
    'payment_method', 'date', 'receipt_no', 'paid_by', 'notes',
    'status', 'created_at', 'business_id', 'updated_at',
  ]),
  cash_movements: new Set([
    'id', 'type', 'description', 'amount', 'date', 'category',
    'payment_method', 'reference', 'recorded_by', 'notes',
    'created_at', 'business_id', 'updated_at', 'payment_id',
    'expense_id', 'movement_date',
  ]),
  stock_movements: new Set([
    'id', 'product_id', 'product_name', 'movement_type', 'quantity',
    'previous_quantity', 'new_quantity', 'reason', 'related_sale_id',
    'related_sale_no', 'created_by', 'created_at', 'business_id',
    'updated_at', 'movement_date',
  ]),
};

// ============================================================
// JSON VALIDATION
// ============================================================

interface ValidationError {
  section: string;
  message: string;
}

function validateBackupJson(data: any): ValidationError[] {
  const errors: ValidationError[] = [];

  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    errors.push({ section: 'Kök', message: 'JSON bir obje olmalıdır, dizi kabul edilmez.' });
    return errors;
  }

  const expectedArrays = [
    'customers', 'products', 'sales', 'sale_items', 'payments',
    'installments', 'deliveries', 'promissory_notes', 'enforcement_cases',
    'expenses', 'cash_movements', 'stock_movements',
  ];

  for (const key of expectedArrays) {
    if (data[key] !== undefined && !Array.isArray(data[key])) {
      errors.push({ section: key, message: `${key} bir dizi (array) olmalıdır.` });
    }
  }

  // customers içinde total_debt_amount tip kontrolü
  if (Array.isArray(data.customers)) {
    for (let i = 0; i < data.customers.length; i++) {
      const c = data.customers[i];
      if (c.total_debt_amount !== undefined && typeof c.total_debt_amount === 'string') {
        c.total_debt_amount = toNumber(c.total_debt_amount);
      }
      if (c.total_sales_amount !== undefined && typeof c.total_sales_amount === 'string') {
        c.total_sales_amount = toNumber(c.total_sales_amount);
      }
      if (c.total_paid_amount !== undefined && typeof c.total_paid_amount === 'string') {
        c.total_paid_amount = toNumber(c.total_paid_amount);
      }
    }
  }

  // promissory_notes içinde tutar normalizasyonu
  if (Array.isArray(data.promissory_notes)) {
    for (let i = 0; i < data.promissory_notes.length; i++) {
      const n = data.promissory_notes[i];
      if (n.amount !== undefined && typeof n.amount === 'string') n.amount = toNumber(n.amount);
      if (n.paid_amount !== undefined && typeof n.paid_amount === 'string') n.paid_amount = toNumber(n.paid_amount);
      if (n.remaining_amount !== undefined && typeof n.remaining_amount === 'string') n.remaining_amount = toNumber(n.remaining_amount);
      
    }
  }

  return errors;
}

// ============================================================
// BATCH UPSERT HELPER
// ============================================================

async function batchUpsert(
  table: string,
  records: any[],
  bizId: string,
  conflictColumn: string = 'id',
): Promise<{ imported: number; updated: number; errors: string[] }> {
  if (!records || records.length === 0) return { imported: 0, updated: 0, errors: [] };

  const columns = TABLE_COLUMNS[table];
  if (!columns) return { imported: 0, updated: 0, errors: [`Bilinmeyen tablo: ${table}`] };

  const errors: string[] = [];
  let imported = 0;
  let updated = 0;

  // Clean & sanitize records
  const cleaned = records.map((r, idx) => {
    const clean: Record<string, any> = { business_id: bizId };
    for (const [key, value] of Object.entries(r)) {
      if (columns.has(key) && value !== undefined) {
        clean[key] = value;
      }
    }
    // Ensure id is a string
    if (clean.id && typeof clean.id !== 'string') {
      clean.id = String(clean.id);
    }
    return clean;
  });

  const BATCH_SIZE = 50;
  for (let i = 0; i < cleaned.length; i += BATCH_SIZE) {
    const batch = cleaned.slice(i, i + BATCH_SIZE);
    try {
      const { error: batchErr } = await supabase
        .from(table)
        .upsert(batch, { onConflict: conflictColumn, ignoreDuplicates: false });

      if (!batchErr) {
        imported += batch.length;
      } else {
        // Batch failed — try one-by-one
        for (const record of batch) {
          try {
            const { error: singleErr } = await supabase
              .from(table)
              .upsert(record, { onConflict: conflictColumn, ignoreDuplicates: false });

            if (singleErr) {
              errors.push(`ID:${record.id?.substring(0, 8)}... → ${handleError(singleErr)}`);
            } else {
              imported++;
            }
          } catch (err: any) {
            errors.push(`ID:${record.id?.substring(0, 8)}... → ${handleError(err)}`);
          }
        }
      }
    } catch (err: any) {
      errors.push(`Batch ${i / BATCH_SIZE + 1}: ${handleError(err)}`);
    }
  }

  return { imported, updated, errors };
}

// ============================================================
// CUSTOMER ID MATCHING
// ============================================================

async function buildCustomerIdMap(
  jsonCustomers: any[],
  bizId: string,
): Promise<{ idMap: Record<string, string>; importResult: ImportTableResult }> {
  const idMap: Record<string, string> = {};
  const errors: string[] = [];
  let imported = 0;
  let updated = 0;

  // Önce mevcut tüm müşterileri çek (eşleştirme için)
  const { data: existingCustomers } = await supabase
    .from('customers')
    .select('id, customer_no, phone, full_name')
    .eq('business_id', bizId);

  const existingById: Record<string, any> = {};
  const existingByNo: Record<string, any> = {};
  const existingByPhone: Record<string, any> = {};
  const existingByName: Record<string, any> = {};

  if (existingCustomers) {
    for (const ec of existingCustomers) {
      existingById[ec.id] = ec;
      if (ec.customer_no) existingByNo[ec.customer_no] = ec;
      if (ec.phone) {
        const cleanPhone = ec.phone.replace(/\s/g, '');
        existingByPhone[cleanPhone] = ec;
      }
      if (ec.full_name) {
        const cleanName = ec.full_name.trim().toLowerCase();
        existingByName[cleanName] = ec;
      }
    }
  }

  const columns = TABLE_COLUMNS.customers!;
  const cleaned = jsonCustomers.map(c => {
    const clean: Record<string, any> = { business_id: bizId };
    for (const [key, value] of Object.entries(c)) {
      if (columns.has(key) && value !== undefined) {
        clean[key] = value;
      }
    }
    if (clean.id && typeof clean.id !== 'string') clean.id = String(clean.id);
    // Tutar alanlarını number yap
    clean.total_sales_amount = toNumber(clean.total_sales_amount);
    clean.total_paid_amount = toNumber(clean.total_paid_amount);
    clean.total_debt_amount = toNumber(clean.total_debt_amount);
    return clean;
  });

  for (const cust of cleaned) {
    const oldId = cust.id;
    try {
      // Önce ID ile deneyelim
      const { data: upserted, error } = await supabase
        .from('customers')
        .upsert(cust, { onConflict: 'id', ignoreDuplicates: false })
        .select('id')
        .single();

      if (!error && upserted) {
        idMap[oldId] = upserted.id;
        imported++;
      } else if (error && error.code === '22P02') {
        // Invalid UUID — try matching by customer_no, phone, or name
        let matchedId: string | null = null;

        if (cust.customer_no && existingByNo[cust.customer_no]) {
          matchedId = existingByNo[cust.customer_no].id;
        } else if (cust.phone) {
          const cleanPhone = cust.phone.replace(/\s/g, '');
          if (existingByPhone[cleanPhone]) {
            matchedId = existingByPhone[cleanPhone].id;
          }
        } else if (cust.full_name) {
          const cleanName = cust.full_name.trim().toLowerCase();
          if (existingByName[cleanName]) {
            matchedId = existingByName[cleanName].id;
          }
        }

        if (matchedId) {
          // Update existing customer
          const { error: updErr } = await supabase
            .from('customers')
            .update({ ...cust, id: undefined })
            .eq('id', matchedId);

          if (!updErr) {
            idMap[oldId] = matchedId;
            updated++;
          } else {
            errors.push(`Müşteri ${cust.full_name || oldId}: ${handleError(updErr)}`);
          }
        } else {
          // Try insert without the invalid ID
          const { data: inserted, error: insErr } = await supabase
            .from('customers')
            .insert({ ...cust, id: undefined })
            .select('id')
            .single();

          if (!insErr && inserted) {
            idMap[oldId] = inserted.id;
            imported++;
          } else {
            errors.push(`Müşteri ${cust.full_name || oldId}: ${handleError(insErr || error)}`);
          }
        }
      } else {
        errors.push(`Müşteri ${cust.full_name || oldId}: ${handleError(error)}`);
      }
    } catch (err: any) {
      errors.push(`Müşteri ${cust.full_name || oldId}: ${handleError(err)}`);
    }
  }

  return {
    idMap,
    importResult: {
      table: 'customers',
      imported,
      updated,
      total: cleaned.length,
      skipped: 0,
      errors,
    },
  };
}

// ============================================================
// MAIN IMPORT FUNCTION
// ============================================================

export async function importBackup(jsonData: any): Promise<ImportBackupResult> {
  const bizId = await getUserBusinessId();
  if (!bizId) {
    return {
      success: false,
      tables: [],
      summary: { totalImported: 0, totalUpdated: 0, totalSkipped: 0, totalErrors: 1 },
      customerIdMap: {},
      noteIdMap: {},
    };
  }

  // Validate
  const validationErrors = validateBackupJson(jsonData);
  if (validationErrors.length > 0) {
    return {
      success: false,
      tables: validationErrors.map(ve => ({
        table: ve.section,
        imported: 0,
        updated: 0,
        total: 0,
        skipped: 0,
        errors: [ve.message],
      })),
      summary: { totalImported: 0, totalUpdated: 0, totalSkipped: 0, totalErrors: validationErrors.length },
      customerIdMap: {},
      noteIdMap: {},
    };
  }

  const allResults: ImportTableResult[] = [];
  let customerIdMap: Record<string, string> = {};
  let noteIdMap: Record<string, string> = {};

  // ============================================================
  // ADIM 1: customers
  // ============================================================
  const customers = Array.isArray(jsonData.customers) ? jsonData.customers : [];
  if (customers.length > 0) {
    const { idMap, importResult } = await buildCustomerIdMap(customers, bizId);
    customerIdMap = idMap;
    allResults.push(importResult);
  } else {
    allResults.push({ table: 'customers', imported: 0, updated: 0, total: 0, skipped: 0, errors: [] });
  }

  // ============================================================
  // ADIM 2: products
  // ============================================================
  const products = Array.isArray(jsonData.products) ? jsonData.products : [];
  if (products.length > 0) {
    const result = await batchUpsert('products', products, bizId, 'id');
    allResults.push({ table: 'products', ...result, total: products.length, skipped: 0 });
  } else {
    allResults.push({ table: 'products', imported: 0, updated: 0, total: 0, skipped: 0, errors: [] });
  }

  // ============================================================
  // ADIM 3: sales
  // ============================================================
  const sales = Array.isArray(jsonData.sales) ? jsonData.sales : [];
  if (sales.length > 0) {
    const mapped = sales.map((s: any) => ({
      ...s,
      customer_id: customerIdMap[s.customer_id] || s.customer_id,
    }));
    const result = await batchUpsert('sales', mapped, bizId, 'id');
    allResults.push({ table: 'sales', ...result, total: sales.length, skipped: 0 });
  } else {
    allResults.push({ table: 'sales', imported: 0, updated: 0, total: 0, skipped: 0, errors: [] });
  }

  // ============================================================
  // ADIM 4: sale_items
  // ============================================================
  const saleItems = Array.isArray(jsonData.sale_items) ? jsonData.sale_items : [];
  if (saleItems.length > 0) {
    const result = await batchUpsert('sale_items', saleItems, bizId, 'id');
    allResults.push({ table: 'sale_items', ...result, total: saleItems.length, skipped: 0 });
  } else {
    allResults.push({ table: 'sale_items', imported: 0, updated: 0, total: 0, skipped: 0, errors: [] });
  }

  // ============================================================
  // ADIM 5: payments
  // ============================================================
  const payments = Array.isArray(jsonData.payments) ? jsonData.payments : [];
  if (payments.length > 0) {
    const mapped = payments.map((p: any) => ({
      ...p,
      customer_id: customerIdMap[p.customer_id] || p.customer_id,
    }));
    const result = await batchUpsert('payments', mapped, bizId, 'id');
    allResults.push({ table: 'payments', ...result, total: payments.length, skipped: 0 });
  } else {
    allResults.push({ table: 'payments', imported: 0, updated: 0, total: 0, skipped: 0, errors: [] });
  }

  // ============================================================
  // ADIM 6: installments
  // ============================================================
  const installments = Array.isArray(jsonData.installments) ? jsonData.installments : [];
  if (installments.length > 0) {
    const mapped = installments.map((i: any) => ({
      ...i,
      customer_id: customerIdMap[i.customer_id] || i.customer_id,
    }));
    const result = await batchUpsert('installments', mapped, bizId, 'id');
    allResults.push({ table: 'installments', ...result, total: installments.length, skipped: 0 });
  } else {
    allResults.push({ table: 'installments', imported: 0, updated: 0, total: 0, skipped: 0, errors: [] });
  }

  // ============================================================
  // ADIM 7: deliveries
  // ============================================================
  const deliveries = Array.isArray(jsonData.deliveries) ? jsonData.deliveries : [];
  if (deliveries.length > 0) {
    const mapped = deliveries.map((d: any) => ({
      ...d,
      customer_id: customerIdMap[d.customer_id] || d.customer_id,
    }));
    const result = await batchUpsert('deliveries', mapped, bizId, 'id');
    allResults.push({ table: 'deliveries', ...result, total: deliveries.length, skipped: 0 });
  } else {
    allResults.push({ table: 'deliveries', imported: 0, updated: 0, total: 0, skipped: 0, errors: [] });
  }

  // ============================================================
  // ADIM 8: promissory_notes (SENETLER - kritik)
  // ============================================================
  const promissoryNotes = Array.isArray(jsonData.promissory_notes) ? jsonData.promissory_notes : [];
  const noteImportResult: ImportTableResult = {
    table: 'promissory_notes',
    imported: 0,
    updated: 0,
    total: promissoryNotes.length,
    skipped: 0,
    errors: [],
  };

  if (promissoryNotes.length > 0) {
    const noteColumns = TABLE_COLUMNS.promissory_notes!;
    const dateFields = ['issue_date', 'due_date', 'purchase_date', 'paid_date'];

    for (const rawNote of promissoryNotes) {
      try {
        const oldNoteId = String(rawNote.id || '');

        // Senet tutarı: amount ?? remaining_amount ?? 0
        const notePrincipal = toNumber(rawNote.amount ?? rawNote.remaining_amount ?? 0);
        const notePaid = toNumber(rawNote.paid_amount ?? 0);

        // remaining_amount: JSON'daki değer varsa kullan, yoksa hesapla
        let noteRemaining: number;
        if (rawNote.remaining_amount !== undefined && rawNote.remaining_amount !== null) {
          noteRemaining = toNumber(rawNote.remaining_amount);
        } else {
          noteRemaining = Math.max(0, notePrincipal - notePaid);
        }

        // Tarihleri null yap
        nullifyEmptyDates(rawNote, dateFields);

        // Temiz kayıt oluştur
        const cleanNote: Record<string, any> = { business_id: bizId };

        for (const [key, value] of Object.entries(rawNote)) {
          if (noteColumns.has(key) && value !== undefined) {
            cleanNote[key] = value;
          }
        }

        // Kolon isimlerini override et
        cleanNote.amount = notePrincipal;
        cleanNote.paid_amount = notePaid;
        cleanNote.remaining_amount = noteRemaining;
        cleanNote.customer_id = customerIdMap[rawNote.customer_id] || rawNote.customer_id;

        // source_type varsayılan
        if (!cleanNote.source_type) {
          cleanNote.source_type = cleanNote.sale_id ? 'linked_sale' : 'legacy_opening_balance';
        }

        // enforcement_status
        if (cleanNote.enforcement_status === undefined) {
          cleanNote.enforcement_status = false;
        }

        // Oto status
        if (!cleanNote.status || cleanNote.status === '') {
          const today = new Date().toISOString().split('T')[0];
          if (noteRemaining <= 0) {
            cleanNote.status = 'Ödendi';
          } else if (cleanNote.due_date && cleanNote.due_date < today) {
            cleanNote.status = 'Gecikti';
          } else {
            cleanNote.status = 'Bekliyor';
          }
        }

        // note_no özel durumu: tekrar yüklemede note_no çakışırsa
        // Önce ID ile dene
        const { data: existingById, error: checkErr } = await supabase
          .from('promissory_notes')
          .select('id')
          .eq('id', cleanNote.id)
          .maybeSingle();

        if (!checkErr && existingById) {
          // Kayıt zaten var, güncelle
          const { error: updErr } = await supabase
            .from('promissory_notes')
            .update(cleanNote)
            .eq('id', cleanNote.id);

          if (updErr) {
            noteImportResult.errors.push(`Senet ${rawNote.note_no || oldNoteId}: ${handleError(updErr)}`);
          } else {
            noteImportResult.updated++;
            noteIdMap[oldNoteId] = cleanNote.id;
          }
        } else {
          // Yeni kayıt veya upsert dene
          const { data: upserted, error: upsertErr } = await supabase
            .from('promissory_notes')
            .upsert(cleanNote, { onConflict: 'id', ignoreDuplicates: false })
            .select('id')
            .single();

          if (upsertErr) {
            // note_no üzerinden tekrar dene (alternatif upsert)
            if (cleanNote.note_no) {
              const { data: byNoteNo, error: byNoteErr } = await supabase
                .from('promissory_notes')
                .select('id')
                .eq('note_no', cleanNote.note_no)
                .eq('business_id', bizId)
                .maybeSingle();

              if (!byNoteErr && byNoteNo) {
                const { error: updErr } = await supabase
                  .from('promissory_notes')
                  .update(cleanNote)
                  .eq('id', byNoteNo.id);

                if (!updErr) {
                  noteImportResult.updated++;
                  noteIdMap[oldNoteId] = byNoteNo.id;
                  continue;
                }
              }
            }

            noteImportResult.errors.push(`Senet ${rawNote.note_no || oldNoteId}: ${handleError(upsertErr)}`);
          } else if (upserted) {
            noteImportResult.imported++;
            noteIdMap[oldNoteId] = upserted.id;
          }
        }
      } catch (err: any) {
        noteImportResult.errors.push(`Senet ${rawNote.note_no || rawNote.id}: ${handleError(err)}`);
      }
    }
  }
  allResults.push(noteImportResult);

  // ============================================================
  // ADIM 9: enforcement_cases (İCRA KAYITLARI)
  // ============================================================
  const enforcementCases = Array.isArray(jsonData.enforcement_cases) ? jsonData.enforcement_cases : [];
  const enfResult: ImportTableResult = {
    table: 'enforcement_cases',
    imported: 0,
    updated: 0,
    total: enforcementCases.length,
    skipped: 0,
    errors: [],
  };

  if (enforcementCases.length > 0) {
    const enfColumns = TABLE_COLUMNS.enforcement_cases!;

    for (const rawCase of enforcementCases) {
      try {
        const cleanCase: Record<string, any> = { business_id: bizId };

        for (const [key, value] of Object.entries(rawCase)) {
          if (enfColumns.has(key) && value !== undefined) {
            cleanCase[key] = value;
          }
        }

        // customer_id eşleştirme
        cleanCase.customer_id = customerIdMap[rawCase.customer_id] || rawCase.customer_id;

        // note_ids → promissory_note_id dönüşümü
        if (rawCase.note_ids && Array.isArray(rawCase.note_ids) && rawCase.note_ids.length > 0) {
          const newNoteId = noteIdMap[rawCase.note_ids[0]];
          if (newNoteId) {
            cleanCase.promissory_note_id = newNoteId;
          }
        }

        // Tarihleri null yap
        nullifyEmptyDates(cleanCase, ['enforcement_date', 'last_contact_date', 'closed_at']);

        // remaining_debt hesapla
        const totalDebt = toNumber(cleanCase.total_debt || 0);
        const paidAfter = toNumber(cleanCase.paid_after_enforcement || 0);
        cleanCase.remaining_debt = Math.max(0, totalDebt - paidAfter);

        const { data: upserted, error: upsertErr } = await supabase
          .from('enforcement_cases')
          .upsert(cleanCase, { onConflict: 'id', ignoreDuplicates: false })
          .select('id')
          .single();

        if (upsertErr) {
          // case_no üzerinden alternatif
          if (cleanCase.case_no) {
            const { data: byCaseNo, error: byCaseErr } = await supabase
              .from('enforcement_cases')
              .select('id')
              .eq('case_no', cleanCase.case_no)
              .eq('business_id', bizId)
              .maybeSingle();

            if (!byCaseErr && byCaseNo) {
              const { error: updErr } = await supabase
                .from('enforcement_cases')
                .update(cleanCase)
                .eq('id', byCaseNo.id);

              if (!updErr) {
                enfResult.updated++;
                // Müşteri status'ünü güncelle
                if (cleanCase.customer_id && cleanCase.status !== 'Tahsil Edildi' && cleanCase.status !== 'Dosya Kapatıldı' && cleanCase.status !== 'İptal Edildi') {
                  await supabase.from('customers').update({ status: 'İcrada' }).eq('id', cleanCase.customer_id);
                }
                continue;
              }
            }
          }

          enfResult.errors.push(`İcra ${rawCase.case_no || rawCase.id}: ${handleError(upsertErr)}`);
        } else if (upserted) {
          enfResult.imported++;
          // Müşteri status'ünü güncelle
          if (cleanCase.customer_id && cleanCase.status !== 'Tahsil Edildi' && cleanCase.status !== 'Dosya Kapatıldı' && cleanCase.status !== 'İptal Edildi') {
            await supabase.from('customers').update({ status: 'İcrada' }).eq('id', cleanCase.customer_id);
          }
        }
      } catch (err: any) {
        enfResult.errors.push(`İcra ${rawCase.case_no || rawCase.id}: ${handleError(err)}`);
      }
    }
  }
  allResults.push(enfResult);

  // ============================================================
  // ADIM 10: expenses
  // ============================================================
  const expenses = Array.isArray(jsonData.expenses) ? jsonData.expenses : [];
  if (expenses.length > 0) {
    const result = await batchUpsert('expenses', expenses, bizId, 'id');
    allResults.push({ table: 'expenses', ...result, total: expenses.length, skipped: 0 });
  } else {
    allResults.push({ table: 'expenses', imported: 0, updated: 0, total: 0, skipped: 0, errors: [] });
  }

  // ============================================================
  // ADIM 11: cash_movements
  // ============================================================
  const cashMovements = Array.isArray(jsonData.cash_movements) ? jsonData.cash_movements : [];
  if (cashMovements.length > 0) {
    const result = await batchUpsert('cash_movements', cashMovements, bizId, 'id');
    allResults.push({ table: 'cash_movements', ...result, total: cashMovements.length, skipped: 0 });
  } else {
    allResults.push({ table: 'cash_movements', imported: 0, updated: 0, total: 0, skipped: 0, errors: [] });
  }

  // ============================================================
  // ADIM 12: stock_movements
  // ============================================================
  const stockMovements = Array.isArray(jsonData.stock_movements) ? jsonData.stock_movements : [];
  if (stockMovements.length > 0) {
    const result = await batchUpsert('stock_movements', stockMovements, bizId, 'id');
    allResults.push({ table: 'stock_movements', ...result, total: stockMovements.length, skipped: 0 });
  } else {
    allResults.push({ table: 'stock_movements', imported: 0, updated: 0, total: 0, skipped: 0, errors: [] });
  }

  // ============================================================
  // ÖZET
  // ============================================================
  const summary = {
    totalImported: allResults.reduce((s, r) => s + r.imported, 0),
    totalUpdated: allResults.reduce((s, r) => s + r.updated, 0),
    totalSkipped: allResults.reduce((s, r) => s + r.skipped, 0),
    totalErrors: allResults.reduce((s, r) => s + r.errors.length, 0),
  };

  return {
    success: summary.totalErrors === 0,
    tables: allResults,
    summary,
    customerIdMap,
    noteIdMap,
  };
}

// ============================================================
// IMPORT SONRASI DOĞRULAMA
// ============================================================

export async function verifyImportBackup(
  customerIdMap: Record<string, string>,
  noteIdMap: Record<string, string>,
): Promise<{ checks: { label: string; passed: boolean; detail: string }[] }> {
  const bizId = await getUserBusinessId();
  const checks: { label: string; passed: boolean; detail: string }[] = [];

  if (!bizId) {
    checks.push({ label: 'İşletme bağlantısı', passed: false, detail: 'İşletme bilgisi alınamadı' });
    return { checks };
  }

  // 1. Müşterilerin total_debt_amount değerleri
  const { data: customers, error: custErr } = await supabase
    .from('customers')
    .select('id, full_name, total_debt_amount')
    .eq('business_id', bizId);

  if (custErr) {
    checks.push({ label: 'Müşteri borç değerleri', passed: false, detail: 'Veritabanı sorgulanamadı' });
  } else {
    const withDebt = (customers || []).filter((c: any) => (c.total_debt_amount || 0) > 0);
    checks.push({
      label: 'Müşteri borç değerleri',
      passed: true,
      detail: `${withDebt.length} müşteride borç kaydı mevcut`,
    });
  }

  // 2. Senetler tablosunda kayıtlar
  const { count: noteCount, error: noteErr } = await supabase
    .from('promissory_notes')
    .select('*', { count: 'exact', head: true })
    .eq('business_id', bizId);

  checks.push({
    label: 'Senet kayıtları',
    passed: !noteErr && (noteCount || 0) > 0,
    detail: noteErr ? 'Sorgulama hatası' : `${noteCount || 0} senet kaydı mevcut`,
  });

  // 3. Senetlerin customer_id bağlantıları
  const { data: notesWithCust, error: ncErr } = await supabase
    .from('promissory_notes')
    .select('id, customer_id')
    .eq('business_id', bizId)
    .limit(10);

  if (ncErr) {
    checks.push({ label: 'Senet-müşteri bağlantıları', passed: false, detail: 'Sorgulama hatası' });
  } else {
    const withCust = (notesWithCust || []).filter((n: any) => n.customer_id);
    checks.push({
      label: 'Senet-müşteri bağlantıları',
      passed: (notesWithCust || []).length > 0 && withCust.length === (notesWithCust || []).length,
      detail: `${withCust.length}/${(notesWithCust || []).length} senette müşteri bağlantısı var`,
    });
  }

  // 4. İcra dosyaları
  const { count: enfCount, error: enfErr } = await supabase
    .from('enforcement_cases')
    .select('*', { count: 'exact', head: true })
    .eq('business_id', bizId);

  checks.push({
    label: 'İcra dosyaları',
    passed: !enfErr,
    detail: enfErr ? 'Sorgulama hatası' : `${enfCount || 0} icra dosyası mevcut`,
  });

  // 5. Dashboard toplam alacak
  const totalDebt = (customers || []).reduce((s: number, c: any) => s + (c.total_debt_amount || 0), 0);
  checks.push({
    label: 'Dashboard toplam alacak',
    passed: true,
    detail: `Toplam: ${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(totalDebt)}`,
  });

  // 6. İcradaki müşteriler
  const enfCustomers = (customers || []).filter((c: any) => c.status === 'İcrada');
  checks.push({
    label: 'İcradaki müşteriler',
    passed: true,
    detail: `${enfCustomers.length} müşteri icra statüsünde`,
  });

  return { checks };
}